import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Home, Search, ArrowRight } from 'lucide-react';
import { useAppContext } from '../store';
import { detectObsoleteUrlPattern } from '../utils/urlRedirects';
import { parseCotationSlug } from '../utils/urlUtils';
import AppinesBanner from './AppinesBanner';
import Footer from './Footer';

interface NotFoundPageProps {
  darkMode?: boolean;
}

const NotFoundPage: React.FC<NotFoundPageProps> = ({ darkMode: propDarkMode }) => {
  const { state } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const darkMode = propDarkMode !== undefined ? propDarkMode : state.ui.darkMode;

  useEffect(() => {
    // Analyser l'URL pour proposer des suggestions intelligentes
    const path = location.pathname;
    const newSuggestions: string[] = [];

    // Vérifier si c'est une URL obsolète détectable
    const autoRedirect = detectObsoleteUrlPattern(path);
    if (autoRedirect) {
      newSuggestions.push(autoRedirect);
    }

    // Essayer de parser comme slug de cotation
    const cotationMatch = path.match(/\/cotations\/([^/]+)$/);
    if (cotationMatch) {
      const slug = cotationMatch[1];
      const parsed = parseCotationSlug(slug);

      if (parsed) {
        // Suggérer la version actuelle
        newSuggestions.push(`/cotations/${parsed.lettre_cle.toLowerCase()}-${parsed.base_cotation_valeur}`);

        // Suggérer la page de la lettre clé
        if (parsed.lettre_cle) {
          newSuggestions.push(`/cotations/actes/${parsed.lettre_cle.toLowerCase()}`);
        }
      }
    }

    // Toujours suggérer la page d'accueil et le simulateur
    if (!newSuggestions.includes('/')) {
      newSuggestions.push('/');
    }
    newSuggestions.push('/simulateur');

    setSuggestions([...new Set(newSuggestions)]);
  }, [location.pathname]);

  const getSuggestionLabel = (path: string): string => {
    if (path === '/') return 'Page d\'accueil';
    if (path === '/simulateur') return 'Simulateur de revenus';
    if (path.startsWith('/cotations/actes/')) {
      const letterKey = path.split('/').pop()?.toUpperCase();
      return `Toutes les cotations ${letterKey}`;
    }
    if (path.startsWith('/cotations/')) {
      return 'Voir cette cotation';
    }
    return path;
  };

  return (
    <div className="min-h-screen transition-colors duration-300" style={{
      background: darkMode
        ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <AppinesBanner className="md:pt-6" />

      <div className="pt-20 md:pt-0 px-4 pb-8">
        <div className="max-w-4xl mx-auto">
          <div className={`text-center py-16 rounded-lg ${
            darkMode ? 'bg-gray-800/50 backdrop-blur-sm' : 'bg-white/80 backdrop-blur-sm'
          } shadow-2xl`}>
            {/* Icône d'erreur */}
            <div className="mb-8 flex justify-center">
              <div className={`p-6 rounded-full ${
                darkMode ? 'bg-orange-500/20' : 'bg-orange-100'
              }`}>
                <AlertTriangle className={`h-16 w-16 ${
                  darkMode ? 'text-orange-400' : 'text-orange-600'
                }`} />
              </div>
            </div>

            {/* Titre */}
            <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              404 - Page non trouvée
            </h1>

            {/* Description */}
            <p className={`text-lg mb-2 ${
              darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              La page que vous recherchez n'existe pas ou a été déplacée.
            </p>

            {/* URL recherchée */}
            <p className={`text-sm mb-8 font-mono ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {location.pathname}
            </p>

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <div className="mb-8">
                <h2 className={`text-xl font-semibold mb-4 ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Essayez ces pages :
                </h2>

                <div className="flex flex-col gap-3 max-w-md mx-auto">
                  {suggestions.map((suggestionPath, index) => (
                    <button
                      key={index}
                      onClick={() => navigate(suggestionPath)}
                      className={`group flex items-center justify-between px-6 py-4 rounded-lg transition-all duration-200 transform hover:scale-105 ${
                        darkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                      } shadow-md hover:shadow-lg`}
                    >
                      <span className="flex items-center gap-3">
                        {suggestionPath === '/' ? (
                          <Home className="h-5 w-5" />
                        ) : (
                          <Search className="h-5 w-5" />
                        )}
                        <span className="font-medium">
                          {getSuggestionLabel(suggestionPath)}
                        </span>
                      </span>
                      <ArrowRight className="h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions principales */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              >
                <Home className="h-5 w-5 mr-2" />
                Retour à l'accueil
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg ${
                  darkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Page précédente
              </button>
            </div>

            {/* Message d'aide */}
            <div className={`mt-12 pt-8 border-t ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <p className={`text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Besoin d'aide ? Utilisez la barre de recherche sur la page d'accueil pour trouver rapidement vos cotations NGAP.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default NotFoundPage;
